import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, TrendingUp, ShieldCheck, Target, Layers,
  PlayCircle, Briefcase, Coins, BarChart3, Globe2, Bell, KanbanSquare, Eye,
} from "lucide-react";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { FunderFAQTeaser } from "../components/landing/FunderFAQTeaser";

const RETURNS = [
  { icon: Target, title: "Verified deals, matched to your budget", body: "Smart matching. Stop wasting time. Set your criteria once and get a curated list of opportunities that perfectly fit your ticket size, sectors, and geography." },
  { icon: ShieldCheck, title: "Score-backed diligence", body: "Every opportunity carries a Capital or Project Readiness Score built from verified documents — so your first look is already a deep look." },
  { icon: Layers, title: "Diversify across asset types", body: "Blend SME equity, debt and project finance — from fintech rounds to solar parks — in one pipeline, across 25 African markets." },
  { icon: TrendingUp, title: "Move first on rising deals", body: "Smart alerts flag when a watched business lifts its score or a new deal crosses your threshold — before the crowd sees it." },
];

const RISK_STEPS = [
  { icon: Briefcase, title: "Sectors", body: "Pick the industries you know and trust — fintech, agri, energy, real estate and more." },
  { icon: Coins, title: "Ticket size", body: "Set your minimum and maximum cheque so every deal is sized for your capital." },
  { icon: BarChart3, title: "Stage & instruments", body: "Choose business stages and how you invest — equity, debt, mezzanine or grants." },
  { icon: Globe2, title: "Geography", body: "Focus on the markets you understand, from a single country to the whole continent." },
];

const PORTFOLIO = [
  { icon: KanbanSquare, title: "Pipeline tracking", body: "Every deal moves through clear stages — viewed, saved, VDR opened, in discussion, closed." },
  { icon: Eye, title: "Watchlists", body: "Follow businesses you're not ready to approach and watch their readiness improve over time." },
  { icon: Bell, title: "Smart alerts", body: "Get notified the moment a score crosses your threshold or a matched deal goes live." },
  { icon: BarChart3, title: "Portfolio analytics", body: "See your deal flow, sector exposure and activity in one clean dashboard." },
];

export default function ForFunders() {
  const nav = useNavigate();
  return (
    <div data-testid="for-funders-page" className="min-h-screen bg-white text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <Navbar />

      {/* Header */}
      <header className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:py-24">
          <Link to="/" data-testid="for-funders-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.3em] text-[#ECC764]">
            Clinq for Funders · Unlock Your Capital Potential
          </p>
          <h1 className="mt-4 max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Deploy capital where it <span className="text-[#7BD3CE]">works hardest.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-white/75 sm:text-lg">
            For individuals, chamas and investment clubs, and institutions — Clinq delivers
            verified, scored African deal flow matched to your risk profile.
          </p>
          <button
            data-testid="for-funders-header-cta"
            onClick={() => nav("/get-started-funder")}
            className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#ECC764] px-7 py-3.5 text-sm font-bold text-[#04302E] transition-all hover:bg-[#D1A330] sm:w-auto"
          >
            Build my investor profile
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </header>

      {/* Maximize Returns */}
      <section data-testid="for-funders-returns" className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">Maximize returns</p>
        <h2 className="mt-3 max-w-2xl text-2xl font-extrabold tracking-tight sm:text-3xl">
          A strategy built on verified information, not gut feel.
        </h2>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {RETURNS.map((r, i) => {
            const Icon = r.icon;
            return (
              <div key={r.title} data-testid={`for-funders-return-${i + 1}`}
                className="group rounded-2xl border border-neutral-200 bg-white p-7 transition-all hover:-translate-y-1 hover:border-[#0A8B85]/40 hover:shadow-xl">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0A8B85]/10 text-[#0A8B85] transition-colors group-hover:bg-[#0A8B85] group-hover:text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-bold">{r.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{r.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Video coming soon */}
      <section data-testid="for-funders-video-section" className="bg-[#F4F8F8]">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-6 py-20 lg:grid-cols-[1fr_1.3fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">See it in action</p>
            <h2 className="mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">
              The funder terminal, in two minutes.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-neutral-600">
              A walkthrough of mandate-matched deal flow, score-backed diligence and the
              NDA-gated data room — coming soon.
            </p>
          </div>
          <div
            data-testid="for-funders-video-placeholder"
            className="relative flex aspect-[16/9] w-full flex-col items-center justify-center overflow-hidden rounded-2xl border border-neutral-200 bg-[#04302E] text-white shadow-[0_30px_60px_-20px_rgba(4,48,46,0.5)]"
          >
            <div aria-hidden="true" className="pointer-events-none absolute -top-20 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-[#0A8B85]/30 blur-3xl" />
            <PlayCircle className="h-14 w-14 text-[#ECC764]" />
            <p className="mt-4 text-sm font-bold uppercase tracking-[0.25em] text-white/80">Video coming soon</p>
            <p className="mt-2 max-w-xs text-center text-xs text-white/50">
              We&apos;re filming the funder walkthrough now. Check back shortly.
            </p>
          </div>
        </div>
      </section>

      {/* Risk Profile onboarding */}
      <section data-testid="for-funders-risk-profile" className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A8B85]">Your risk profile</p>
        <h2 className="mt-3 max-w-2xl text-2xl font-extrabold tracking-tight sm:text-3xl">
          Onboarding takes five minutes. Your mandate does the rest.
        </h2>
        <p className="mt-4 max-w-2xl text-base text-neutral-600">
          Four quick questions build your investor risk profile. From then on, every deal
          Clinq shows you already fits it — and you can refine it any time.
        </p>
        <div className="relative mt-12">
          <div aria-hidden="true" className="absolute left-0 right-0 top-6 hidden h-px bg-gradient-to-r from-transparent via-[#0A8B85]/40 to-transparent lg:block" />
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {RISK_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} data-testid={`for-funders-risk-step-${i + 1}`} className="relative">
                  <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#0A8B85] bg-white text-sm font-extrabold text-[#0A8B85] shadow-sm">
                    0{i + 1}
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <Icon className="h-4 w-4 text-[#0A8B85]" />
                    <h3 className="text-base font-bold">{s.title}</h3>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portfolio tracking */}
      <section data-testid="for-funders-portfolio" className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#ECC764]">Portfolio tracking</p>
          <h2 className="mt-3 max-w-2xl text-2xl font-extrabold tracking-tight sm:text-3xl">
            From first look to closed deal — tracked in one place.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {PORTFOLIO.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="bg-[#04302E] p-7 transition-colors hover:bg-white/[0.04]">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#ECC764]/15 text-[#ECC764]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-base font-bold">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/65">{p.body}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-5">
            <button
              data-testid="for-funders-footer-cta"
              onClick={() => nav("/get-started-funder")}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#ECC764] px-7 py-3.5 text-sm font-bold text-[#04302E] transition-all hover:bg-[#D1A330] sm:w-auto"
            >
              Start exploring deal flow
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="text-sm text-white/50">Full access while we finalise plans — pricing announced soon</p>
          </div>
        </div>
      </section>

      <FunderFAQTeaser />

      <Footer />
    </div>
  );
}
